import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Alert } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import * as settings from "../settings";

export default function ProbateContact({ isMobile }) {
    const { caseNumber: urlCaseNumber } = useParams();
    const [caseNumber, setCaseNumber] = useState('');
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    // Store raw digits for submission if needed, or just rely on stripping non-digits later
    const [rawDigits, setRawDigits] = useState('');
    const inputRef = React.useRef(null);

    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState('');
    const [agreed, setAgreed] = useState(false);

    useEffect(() => {
        if (urlCaseNumber) {
            const truncatedCaseNumber = urlCaseNumber.slice(0, 30);
            setCaseNumber(truncatedCaseNumber);
            // Fetch to live endpoint for tracking
            const screenSize = `${window.screen.width}x${window.screen.height}`;
            fetch(`https://pembre.com/probate/probate-contact-endpoint.php?type=track&caseNumber=${encodeURIComponent(truncatedCaseNumber)}&screen_size=${encodeURIComponent(screenSize)}`)
                .then(res => console.log('Tracking response:', res))
                .catch(err => console.error('Tracking fetch failed:', err));
        }
    }, [urlCaseNumber]);

    // Format digits into (xxx) xxx-xxxx
    const formatPhoneNumber = (digits) => {
        if (!digits.length) return "";
        let part1 = digits.slice(0, 3);
        let part2 = digits.slice(3, 6);
        let part3 = digits.slice(6, 10);

        let result = "(" + part1;
        if (digits.length > 3) {
            result += ") " + part2;
        }
        if (digits.length > 6) {
            result += "-" + part3;
        }
        return result;
    };

    const handlePhoneChange = (e) => {
        const inputValue = e.target.value;
        const selectionStart = e.target.selectionStart;
        const digitsBeforeCaret = (inputValue.slice(0, selectionStart).match(/\d/g) || []).length;

        const digits = inputValue.replace(/\D/g, "").slice(0, 10);
        setRawDigits(digits);

        const formatted = formatPhoneNumber(digits);
        setPhone(formatted);

        // Re-position the caret
        requestAnimationFrame(() => {
            if (!inputRef.current) return;

            let newCaretPos = 0;
            let count = 0;
            while (newCaretPos < formatted.length && count < digitsBeforeCaret) {
                if (/\d/.test(formatted[newCaretPos])) {
                    count++;
                }
                newCaretPos++;
            }
            inputRef.current.setSelectionRange(newCaretPos, newCaretPos);
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!caseNumber || !name || !phone || !email || !message) {
            setError('All fields are required.');
            return;
        }

        if (!agreed) {
            setError('You must agree to the terms to submit.');
            return;
        }

        // Dummy submit
        try {
            console.log('Submitting form:', { caseNumber, name, phone: rawDigits, email, message });

            const screenSize = `${window.screen.width}x${window.screen.height}`;

            // fetch to live endpoint
            await fetch('https://pembre.com/probate/probate-contact-endpoint.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    caseNumber,
                    name,
                    phone: rawDigits,
                    email,
                    message,
                    screen_size: screenSize
                })
            }).catch(err => console.error('Submit fetch failed:', err));

            // Simulate network request delay if needed, though fetch might fail fast
            // await new Promise(resolve => setTimeout(resolve, 500));
            setSubmitted(true);
        } catch (err) {
            setError('Something went wrong. Please try again.');
        }
    };

    return (
        <>
            {/* Hero Section */}
            <div
                className="bg-dark text-light"
                style={{
                    minHeight: '50vh',
                    backgroundImage: `
            linear-gradient(
              to bottom,
              rgba(0, 0, 0, 0.8),
              rgba(0, 0, 0, 0.6)
            ),
url(${settings.SITEURL}/hero-bg.jpg)          `,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    display: 'flex',
                    alignItems: 'center'
                }}
            >
                <Container>
                    <Row className="justify-content-center text-center">
                        <Col lg={10}>
                            <h1 className="display-4 fw-bold">
                                Comprehensive Property Protection Services
                            </h1>
                            <h2 className="lead mt-3 fs-3 text-white-50">
                                Specialized support for probate estates: Immediate security, 24/7 emergency response, and cost-effective property disposition.
                            </h2>
                        </Col>
                    </Row>
                </Container>
            </div>

            {/* Contact Form Section */}
            <Container className="py-5" style={{ maxWidth: '800px' }}>
                <h2 className="mb-4 text-center">Contact Us</h2>
                {submitted ? (
                    <Alert variant="success">
                        Thank you! Your message has been sent. We will be in touch shortly.
                    </Alert>
                ) : (
                    <Form onSubmit={handleSubmit}>
                        {error && <Alert variant="danger">{error}</Alert>}

                        <Form.Group className="mb-3" controlId="formCaseNumber">
                            <Form.Label>Case Number</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter Case Number"
                                value={caseNumber}
                                onChange={(e) => setCaseNumber(e.target.value.slice(0, 30))}
                                maxLength={30}
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="formName">
                            <Form.Label>Name</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Your Name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="formPhone">
                            <Form.Label>Phone Number</Form.Label>
                            <Form.Control
                                type="tel"
                                placeholder="(555) 123-4567"
                                value={phone}
                                onChange={handlePhoneChange}
                                ref={inputRef}
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="formEmail">
                            <Form.Label>Email Address</Form.Label>
                            <Form.Control
                                type="email"
                                placeholder="name@example.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="formMessage">
                            <Form.Label>Message</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={5}
                                placeholder="How can we assist you?"
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="formConsent">
                            <Form.Check
                                type="checkbox"
                                label={
                                    <span className="small text-muted">
                                        I agree to be contacted by Pembre Realty LLC via call, email, and text. To opt-out subsequently, you can reply 'STOP' at any time or click the unsubscribe link in the emails. Message and data rates may apply.
                                    </span>
                                }
                                checked={agreed}
                                onChange={(e) => setAgreed(e.target.checked)}
                            />
                        </Form.Group>

                        <div className="d-grid gap-2">
                            <Button variant="primary" size="lg" type="submit">
                                Submit
                            </Button>
                        </div>
                    </Form>
                )}
            </Container>
        </>
    );
}
